// Biblioteki eksportu (docx, jspdf, jspdf-autotable) są ładowane dynamicznie
// przy pierwszym eksporcie (lazy-load), żeby nie pompować głównego bundle.
// Poniżej wyłącznie importy typów — nie trafiają do bundle.
import type { Paragraph as DocxParagraph, TableRow as DocxTableRow } from 'docx';
import type { jsPDF } from 'jspdf';

type DocxModule = typeof import('docx');

const EXPORT_MODULE_LOAD_ERROR = 'Nie udało się załadować modułu eksportu';

// =============================================================
// EXPORT_PALETTE — stała paleta eksportów (PDF/DOCX), zawsze LIGHT.
// Zasada: raport NIE podąża za motywem ekranu — dokument kliniczny
// renderuje się jak papier, niezależnie od dark mode.
// Wartości = paleta light „Warm paper" (PLAN-REFACTOR.md, sekcja 3a):
// neutralne slate/gray → stone, dawne indigo/blue nagłówków → accent
// strong (emerald-700). Kolory domenowe WISC-V bez zmian (semantyka
// kliniczna).
// =============================================================
export const EXPORT_PALETTE = {
  // Tekst (stone)
  textPrimary:   '#292524',  // stone-800 — tekst zasadniczy
  textSecondary: '#57534E',  // stone-600 — tekst pomocniczy, disclaimer
  textTertiary:  '#78716C',  // stone-500 — metadane, stopka
  // Powierzchnie / linie
  white:         '#FFFFFF',
  border:        '#D6D3D1',  // stone-300 — obramowania zewnętrzne tabel
  borderSubtle:  '#E7E5E4',  // stone-200 — linie wewnętrzne tabel, linia stopki
  headerBg:      '#F5F5F4',  // stone-100 — tło nagłówków tabel (paper)
  rowAlt:        '#FAFAF9',  // stone-50  — wiersze naprzemienne (zebra)
  accentSoft:    '#ECFDF5',  // emerald-50 — zebra tabeli podtestów
  // Akcent + semantyczne (light)
  accent:        '#047857',  // emerald-700 (accent-strong) — nagłówki sekcji, head tabel
  success:       '#047857',  // mocne strony
  warning:       '#B45309',  // zalecenia dla nauczycieli
  error:         '#DC2626',  // obszary wymagające wsparcia
  info:          '#1D4ED8',  // funkcjonowanie w klasie
  // Domeny WISC-V (bez zmian — semantyka kliniczna)
  domainVci:  '#3B82F6',
  domainVsi:  '#10B981',
  domainFri:  '#8B5CF6',
  domainWmi:  '#F59E0B',
  domainPsi:  '#EF4444',
  domainFsiq: '#6B7280',
} as const;

// Biblioteka docx przyjmuje kolory jako hex BEZ '#'.
const docxHex = (hex: string): string => hex.replace('#', '');

// Wymóg prawny (RODO / narzędzie kliniczne): każdy eksport musi zawierać poniższe zastrzeżenie.
export const AI_DISCLAIMER =
  'Raport został wygenerowany z wykorzystaniem AI. Wszystkie interpretacje wymagają weryfikacji i akceptacji przez prowadzącego specjalistę. System stanowi wyłącznie wsparcie decyzyjne.';

export interface ExportData {
  patientInfo: {
    name: string;
    birthDate: string;
    testDate: string;
    examiner: string;
    institution: string;
  };
  compositeScores: Record<string, string>;
  subtestScores: Record<string, string>;
  interpretation: {
    general_assessment: string;
    strengths: string;
    areas_for_support: string;
    classroom_implications: string;
    teacher_recommendations: string[] | string;
    parent_recommendations: string[] | string;
  };
  compositeNames: Record<string, string>;
  subtestNames: Record<string, string>;
  getScoreInterpretation: (score: string | number, type?: string) => string;
}

export class ExportUtils {

  // Guard: zapobiega podwójnemu uruchomieniu eksportu (np. double-click).
  private static exportInProgress = false;

  // Wspólna nazwa pliku raportu dla wszystkich formatów: Raport_WISC-V_{imię}_{data}.{ext}
  static getReportFilename(patientName: string, ext: string): string {
    return `Raport_WISC-V_${patientName || 'Pacjent'}_${new Date().toISOString().split('T')[0]}.${ext}`;
  }

  // Wspólny mechanizm pobrania: Blob -> <a download> -> click (używany przez TXT/MD/DOCX).
  private static downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // Wspólny helper (TXT/MD/PDF): przefiltrowane wiersze [nazwa, wynik, interpretacja]
  // dla wskaźników złożonych (type pominięty) lub podtestów (type = 'subtest').
  private static collectScoreRows(
    scores: Record<string, string>,
    names: Record<string, string>,
    getScoreInterpretation: ExportData['getScoreInterpretation'],
    type?: string
  ): Array<[string, string, string]> {
    return Object.entries(scores)
      .filter(([, value]) => value && value !== '')
      .map(([key, value]) => [
        names[key] || key,
        value,
        type ? getScoreInterpretation(value, type) : getScoreInterpretation(value),
      ]);
  }

  // Lista linii "- Nazwa: wynik (interpretacja)" — sekcje wyników w raporcie TXT.
  static buildScoresLines(
    scores: Record<string, string>,
    names: Record<string, string>,
    getScoreInterpretation: ExportData['getScoreInterpretation'],
    type?: string
  ): string {
    return this.collectScoreRows(scores, names, getScoreInterpretation, type)
      .map(([name, value, interpretation]) => `- ${name}: ${value} (${interpretation})`)
      .join('\n');
  }

  // Export to TXT — czysty template string, zero bibliotek zewnętrznych (bez lazy-loadu).
  static exportToTxt(data: ExportData): void {
    const reportContent = `
RAPORT DIAGNOSTYCZNY WISC-V
============================

DANE PACJENTA:
- Imię i nazwisko: ${data.patientInfo.name || 'Brak danych'}
- Data urodzenia: ${data.patientInfo.birthDate || 'Brak danych'}
- Data badania: ${data.patientInfo.testDate || 'Brak danych'}
- Badający: ${data.patientInfo.examiner || 'Brak danych'}
- Instytucja: ${data.patientInfo.institution || 'Brak danych'}

WYNIKI WSKAŹNIKÓW ZŁOŻONYCH:
${this.buildScoresLines(data.compositeScores, data.compositeNames, data.getScoreInterpretation)}

WYNIKI PODTESTÓW:
${this.buildScoresLines(data.subtestScores, data.subtestNames, data.getScoreInterpretation, 'subtest')}

INTERPRETACJA:

OGÓLNA OCENA:
${data.interpretation.general_assessment}

MOCNE STRONY:
${data.interpretation.strengths}

OBSZARY TRUDNOŚCI:
${data.interpretation.areas_for_support}

FUNKCJONOWANIE SZKOLNE:
${data.interpretation.classroom_implications}

ZALECENIA DLA NAUCZYCIELI:
${Array.isArray(data.interpretation.teacher_recommendations)
  ? data.interpretation.teacher_recommendations.map(rec => `- ${rec}`).join('\n')
  : data.interpretation.teacher_recommendations}

ZALECENIA DLA RODZICÓW:
${Array.isArray(data.interpretation.parent_recommendations)
  ? data.interpretation.parent_recommendations.map(rec => `- ${rec}`).join('\n')
  : data.interpretation.parent_recommendations}

ZASTRZEŻENIE:
${AI_DISCLAIMER}

Data wygenerowania: ${new Date().toLocaleString('pl-PL')}
        `;

    const blob = new Blob([reportContent], { type: 'text/plain;charset=utf-8' });
    this.downloadBlob(blob, this.getReportFilename(data.patientInfo.name, 'txt'));
  }

  // Lazy-load biblioteki docx (dopiero przy pierwszym eksporcie DOCX).
  private static async loadDocx(): Promise<DocxModule> {
    try {
      return await import('docx');
    } catch (err) {
      console.error('Błąd ładowania modułu docx:', err);
      throw new Error(EXPORT_MODULE_LOAD_ERROR);
    }
  }

  // Lazy-load jsPDF + plugin jspdf-autotable (side-effect rozszerzający jsPDF)
  // + osadzone fonty Lato z polskimi znakami (standardowa helvetica jsPDF
  // ma tylko WinAnsi — ą/ć/ę/ł/ń/ó/ś/ź/ż renderowały się błędnie).
  // Wszystko w lazy chunkach — nic z tego nie trafia do głównego bundle.
  private static async loadJsPDF(): Promise<{
    JsPDF: typeof jsPDF;
    fonts: { regular: string; bold: string };
  }> {
    try {
      const [jspdfModule, , fontsModule] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
        import('./pdfFonts'),
      ]);
      return {
        JsPDF: jspdfModule.jsPDF,
        fonts: {
          regular: fontsModule.LATO_REGULAR_B64,
          bold: fontsModule.LATO_BOLD_B64,
        },
      };
    } catch (err) {
      console.error('Błąd ładowania modułu jspdf:', err);
      throw new Error(EXPORT_MODULE_LOAD_ERROR);
    }
  }

  // Export to DOCX
  static async exportToDocx(data: ExportData): Promise<void> {
    if (this.exportInProgress) return;
    this.exportInProgress = true;
    try {
      await this.doExportToDocx(data);
    } finally {
      this.exportInProgress = false;
    }
  }

  private static async doExportToDocx(data: ExportData): Promise<void> {
    const docx = await this.loadDocx();
    const { Document, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType, Packer, Footer, Header, BorderStyle } = docx;
    const doc = new Document({
      creator: "WISC-V Diagnostyka AI",
      title: "Raport Diagnostyczny WISC-V",
      description: "Profesjonalny raport diagnostyczny oparty na WISC-V",
      sections: [{
        properties: {
          page: {
            margin: {
              top: 1440, // 1 inch = 1440 twips
              right: 1440,
              bottom: 1440,
              left: 1440,
            },
          },
        },
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: "RAPORT DIAGNOSTYCZNY WISC-V",
                    size: 24,
                    bold: true,
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { after: 200 },
              }),
            ],
          }),
        },
        footers: {
          default: new Footer({
            children: [
              new Paragraph({
                children: [
                  new TextRun({
                    text: `Wygenerowano: ${new Date().toLocaleDateString('pl-PL')} | Strona `,
                    size: 20,
                  }),
                ],
                alignment: AlignmentType.CENTER,
                spacing: { before: 200 },
              }),
            ],
          }),
        },
        children: [
          // Title
          new Paragraph({
            text: "RAPORT DIAGNOSTYCZNY WISC-V",
            heading: HeadingLevel.TITLE,
            alignment: AlignmentType.CENTER,
            spacing: { after: 400 }
          }),

          // Patient Data Section
          new Paragraph({
            text: "DANE PACJENTA",
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 }
          }),
          
          new Paragraph({
            children: [
              new TextRun({ text: "Imię i nazwisko: ", bold: true }),
              new TextRun({ text: data.patientInfo.name || 'Brak danych' })
            ],
            spacing: { after: 100 }
          }),
          
          new Paragraph({
            children: [
              new TextRun({ text: "Data urodzenia: ", bold: true }),
              new TextRun({ text: data.patientInfo.birthDate || 'Brak danych' })
            ],
            spacing: { after: 100 }
          }),
          
          new Paragraph({
            children: [
              new TextRun({ text: "Data badania: ", bold: true }),
              new TextRun({ text: data.patientInfo.testDate || 'Brak danych' })
            ],
            spacing: { after: 100 }
          }),
          
          new Paragraph({
            children: [
              new TextRun({ text: "Badający: ", bold: true }),
              new TextRun({ text: data.patientInfo.examiner || 'Brak danych' })
            ],
            spacing: { after: 100 }
          }),
          
          new Paragraph({
            children: [
              new TextRun({ text: "Instytucja: ", bold: true }),
              new TextRun({ text: data.patientInfo.institution || 'Brak danych' })
            ],
            spacing: { after: 400 }
          }),

          // Composite Scores Section
          new Paragraph({
            text: "WYNIKI WSKAŹNIKÓW ZŁOŻONYCH",
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 400, after: 200 }
          }),
          
          // Composite Scores Table
          new Table({
            width: {
              size: 100,
              type: WidthType.PERCENTAGE
            },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.border) },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.border) },
              left: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.border) },
              right: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.border) },
              insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.borderSubtle) },
              insideVertical: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.borderSubtle) },
            },
            rows: [
              new TableRow({
                tableHeader: true,
                children: [
                  new TableCell({
                    children: [new Paragraph({ 
                      text: "Wskaźnik", 
                      alignment: AlignmentType.CENTER,
                      spacing: { before: 100, after: 100 }
                    })],
                    shading: { fill: docxHex(EXPORT_PALETTE.headerBg), type: "solid" },
                    width: { size: 40, type: WidthType.PERCENTAGE },
                  }),
                  new TableCell({
                    children: [new Paragraph({ 
                      text: "Wynik", 
                      alignment: AlignmentType.CENTER,
                      spacing: { before: 100, after: 100 }
                    })],
                    shading: { fill: docxHex(EXPORT_PALETTE.headerBg), type: "solid" },
                    width: { size: 20, type: WidthType.PERCENTAGE },
                  }),
                  new TableCell({
                    children: [new Paragraph({ 
                      text: "Interpretacja", 
                      alignment: AlignmentType.CENTER,
                      spacing: { before: 100, after: 100 }
                    })],
                    shading: { fill: docxHex(EXPORT_PALETTE.headerBg), type: "solid" },
                    width: { size: 40, type: WidthType.PERCENTAGE },
                  })
                ]
              }),
              ...Object.entries(data.compositeScores).reduce((acc, [key, value]) => {
                if (value && value !== '') {
                  const index = acc.length;
                  acc.push(new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({
                          text: data.compositeNames[key] || key,
                          spacing: { before: 100, after: 100 }
                        })],
                        shading: { fill: index % 2 === 0 ? docxHex(EXPORT_PALETTE.rowAlt) : docxHex(EXPORT_PALETTE.white), type: "solid" },
                      }),
                      new TableCell({
                        children: [new Paragraph({
                          text: value,
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 100, after: 100 }
                        })],
                        shading: { fill: index % 2 === 0 ? docxHex(EXPORT_PALETTE.rowAlt) : docxHex(EXPORT_PALETTE.white), type: "solid" },
                      }),
                      new TableCell({
                        children: [new Paragraph({
                          text: data.getScoreInterpretation(value),
                          spacing: { before: 100, after: 100 }
                        })],
                        shading: { fill: index % 2 === 0 ? docxHex(EXPORT_PALETTE.rowAlt) : docxHex(EXPORT_PALETTE.white), type: "solid" },
                      })
                    ]
                  }));
                }
                return acc;
              }, [] as DocxTableRow[])
            ]
          }),

          // Subtests Table
          new Paragraph({
            text: "WYNIKI PODTESTÓW",
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 600, after: 200 }
          }),
          
          new Table({
            width: {
              size: 100,
              type: WidthType.PERCENTAGE
            },
            borders: {
              top: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.border) },
              bottom: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.border) },
              left: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.border) },
              right: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.border) },
              insideHorizontal: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.borderSubtle) },
              insideVertical: { style: BorderStyle.SINGLE, size: 1, color: docxHex(EXPORT_PALETTE.borderSubtle) },
            },
            rows: [
              new TableRow({
                tableHeader: true,
                children: [
                  new TableCell({
                    children: [new Paragraph({ 
                      text: "Podtest", 
                      alignment: AlignmentType.CENTER,
                      spacing: { before: 100, after: 100 }
                    })],
                    shading: { fill: docxHex(EXPORT_PALETTE.headerBg), type: "solid" },
                    width: { size: 40, type: WidthType.PERCENTAGE },
                  }),
                  new TableCell({
                    children: [new Paragraph({ 
                      text: "Wynik", 
                      alignment: AlignmentType.CENTER,
                      spacing: { before: 100, after: 100 }
                    })],
                    shading: { fill: docxHex(EXPORT_PALETTE.headerBg), type: "solid" },
                    width: { size: 20, type: WidthType.PERCENTAGE },
                  }),
                  new TableCell({
                    children: [new Paragraph({ 
                      text: "Interpretacja", 
                      alignment: AlignmentType.CENTER,
                      spacing: { before: 100, after: 100 }
                    })],
                    shading: { fill: docxHex(EXPORT_PALETTE.headerBg), type: "solid" },
                    width: { size: 40, type: WidthType.PERCENTAGE },
                  })
                ]
              }),
              ...Object.entries(data.subtestScores).reduce((acc, [key, value]) => {
                if (value && value !== '') {
                  const index = acc.length;
                  acc.push(new TableRow({
                    children: [
                      new TableCell({
                        children: [new Paragraph({
                          text: data.subtestNames[key] || key,
                          spacing: { before: 100, after: 100 }
                        })],
                        shading: { fill: index % 2 === 0 ? docxHex(EXPORT_PALETTE.accentSoft) : docxHex(EXPORT_PALETTE.white), type: "solid" },
                      }),
                      new TableCell({
                        children: [new Paragraph({
                          text: value,
                          alignment: AlignmentType.CENTER,
                          spacing: { before: 100, after: 100 }
                        })],
                        shading: { fill: index % 2 === 0 ? docxHex(EXPORT_PALETTE.accentSoft) : docxHex(EXPORT_PALETTE.white), type: "solid" },
                      }),
                      new TableCell({
                        children: [new Paragraph({
                          text: data.getScoreInterpretation(value, 'subtest'),
                          spacing: { before: 100, after: 100 }
                        })],
                        shading: { fill: index % 2 === 0 ? docxHex(EXPORT_PALETTE.accentSoft) : docxHex(EXPORT_PALETTE.white), type: "solid" },
                      })
                    ]
                  }));
                }
                return acc;
              }, [] as DocxTableRow[])
            ]
          }),

          // Interpretation Section
          new Paragraph({
            text: "INTERPRETACJA WYNIKÓW",
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 600, after: 300 }
          }),
          
          new Paragraph({
            children: [
              new TextRun({
                text: "Ogólna ocena",
                bold: true,
                size: 28,
                color: docxHex(EXPORT_PALETTE.textPrimary)
              })
            ],
            spacing: { before: 300, after: 150 }
          }),
          
          new Paragraph({
            text: data.interpretation.general_assessment || 'Brak danych',
            spacing: { after: 300, before: 0 },
            indent: { left: 200 }
          }),
          
          new Paragraph({
            children: [
              new TextRun({
                text: "Mocne strony",
                bold: true,
                size: 28,
                color: docxHex(EXPORT_PALETTE.success)
              })
            ],
            spacing: { before: 300, after: 150 }
          }),
          
          new Paragraph({
            text: data.interpretation.strengths || 'Brak danych',
            spacing: { after: 300, before: 0 },
            indent: { left: 200 }
          }),
          
          new Paragraph({
            children: [
              new TextRun({
                text: "Obszary wymagające wsparcia",
                bold: true,
                size: 28,
                color: docxHex(EXPORT_PALETTE.error)
              })
            ],
            spacing: { before: 300, after: 150 }
          }),
          
          new Paragraph({
            text: data.interpretation.areas_for_support || 'Brak danych',
            spacing: { after: 300, before: 0 },
            indent: { left: 200 }
          }),
          
          new Paragraph({
            children: [
              new TextRun({
                text: "Funkcjonowanie w klasie",
                bold: true,
                size: 28,
                color: docxHex(EXPORT_PALETTE.info)
              })
            ],
            spacing: { before: 300, after: 150 }
          }),
          
          new Paragraph({
            text: data.interpretation.classroom_implications || 'Brak danych',
            spacing: { after: 300, before: 0 },
            indent: { left: 200 }
          }),
          
          new Paragraph({
            children: [
              new TextRun({
                text: "Zalecenia dla nauczycieli",
                bold: true,
                size: 28,
                color: docxHex(EXPORT_PALETTE.warning)
              })
            ],
            spacing: { before: 300, after: 150 }
          }),
          
          ...ExportUtils.formatRecommendations(docx, data.interpretation.teacher_recommendations),
          
          new Paragraph({
            children: [
              new TextRun({
                text: "Zalecenia dla rodziców",
                bold: true,
                size: 28,
                color: docxHex(EXPORT_PALETTE.textSecondary)
              })
            ],
            spacing: { before: 300, after: 150 }
          }),
          
          ...ExportUtils.formatRecommendations(docx, data.interpretation.parent_recommendations),

          // AI Disclaimer (legal requirement)
          new Paragraph({
            children: [
              new TextRun({
                text: "ZASTRZEŻENIE",
                bold: true,
                size: 24,
                color: docxHex(EXPORT_PALETTE.textTertiary)
              })
            ],
            spacing: { before: 600, after: 150 }
          }),

          new Paragraph({
            children: [
              new TextRun({
                text: AI_DISCLAIMER,
                italics: true,
                size: 20,
                color: docxHex(EXPORT_PALETTE.textSecondary)
              })
            ],
            spacing: { after: 200 }
          })
        ]
      }]
    });

    // Generate and download. Packer.toBuffer jest Node-only — w przeglądarce
    // JSZip rzuca "nodebuffer is not supported by this platform". toBlob to
    // ścieżka przeglądarkowa (zwraca gotowy Blob z poprawnym typem).
    const blob = await Packer.toBlob(doc);
    this.downloadBlob(blob, this.getReportFilename(data.patientInfo.name, 'docx'));
  }

  // Export to Markdown
  static exportToMarkdown(data: ExportData): void {
    const teacherRecs = Array.isArray(data.interpretation.teacher_recommendations) 
      ? data.interpretation.teacher_recommendations.map(rec => `- ${rec}`).join('\n')
      : data.interpretation.teacher_recommendations;
    
    const parentRecs = Array.isArray(data.interpretation.parent_recommendations)
      ? data.interpretation.parent_recommendations.map(rec => `- ${rec}`).join('\n')
      : data.interpretation.parent_recommendations;

    const compositeScoresTable = this.collectScoreRows(data.compositeScores, data.compositeNames, data.getScoreInterpretation)
      .map(([name, value, interpretation]) => `| ${name} | ${value} | ${interpretation} |`)
      .join('\n');

    const subtestScoresTable = this.collectScoreRows(data.subtestScores, data.subtestNames, data.getScoreInterpretation, 'subtest')
      .map(([name, value, interpretation]) => `| ${name} | ${value} | ${interpretation} |`)
      .join('\n');

    const markdown = `# RAPORT DIAGNOSTYCZNY WISC-V

## DANE PACJENTA

- **Imię i nazwisko:** ${data.patientInfo.name || 'Brak danych'}
- **Data urodzenia:** ${data.patientInfo.birthDate || 'Brak danych'}
- **Data badania:** ${data.patientInfo.testDate || 'Brak danych'}
- **Badający:** ${data.patientInfo.examiner || 'Brak danych'}
- **Instytucja:** ${data.patientInfo.institution || 'Brak danych'}

## WYNIKI WSKAŹNIKÓW ZŁOŻONYCH

| Wskaźnik | Wynik | Interpretacja |
|----------|-------|---------------|
${compositeScoresTable}

## WYNIKI PODTESTÓW

| Podtest | Wynik | Interpretacja |
|---------|-------|---------------|
${subtestScoresTable}

## INTERPRETACJA WYNIKÓW

### Ogólna ocena

${data.interpretation.general_assessment}

### Mocne strony

${data.interpretation.strengths}

### Obszary wymagające wsparcia

${data.interpretation.areas_for_support}

### Funkcjonowanie w klasie

${data.interpretation.classroom_implications}

### Zalecenia dla nauczycieli

${teacherRecs}

### Zalecenia dla rodziców

${parentRecs}

---

> **Zastrzeżenie:** ${AI_DISCLAIMER}

*Data wygenerowania: ${new Date().toLocaleDateString('pl-PL')}*
`;

    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    this.downloadBlob(blob, this.getReportFilename(data.patientInfo.name, 'md'));
  }

  // Export to PDF
  static async exportToPDF(data: ExportData): Promise<void> {
    if (this.exportInProgress) return;
    this.exportInProgress = true;
    try {
      await this.doExportToPDF(data);
    } finally {
      this.exportInProgress = false;
    }
  }

  private static async doExportToPDF(data: ExportData): Promise<void> {
    const pdf = await this.buildPdfDocument(data);
    pdf.save(this.getReportFilename(data.patientInfo.name, 'pdf'));
  }

  // Skrócona wersja disclaimera do stopki każdej strony.
  // Pełny AI_DISCLAIMER pozostaje w sekcji ZASTRZEŻENIE na końcu raportu.
  private static readonly PDF_FOOTER_DISCLAIMER =
    'Raport wygenerowany z wykorzystaniem AI — wymaga weryfikacji przez prowadzącego specjalistę.';

  // Buduje kompletny dokument PDF (bez zapisu) — publiczne, żeby skrypt
  // regresji wizualnej (scripts/render-sample-pdf.mjs) mógł renderować
  // dokładnie ten sam dokument, który dostaje użytkownik.
  static async buildPdfDocument(data: ExportData): Promise<jsPDF> {
    const { JsPDF, fonts } = await this.loadJsPDF();
    const pdf = new JsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4'
    });

    // Osadzenie Lato (regular + bold) — pełne polskie glify.
    pdf.addFileToVFS('Lato-Regular.ttf', fonts.regular);
    pdf.addFont('Lato-Regular.ttf', 'Lato', 'normal');
    pdf.addFileToVFS('Lato-Bold.ttf', fonts.bold);
    pdf.addFont('Lato-Bold.ttf', 'Lato', 'bold');
    pdf.setFont('Lato', 'normal');
    pdf.setLanguage('pl');

    // Wymiary i siatka strony
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const M = { top: 20, left: 18, right: 18, bottom: 24 };
    const contentW = pageWidth - M.left - M.right;
    const nextPageTop = 26; // od 2. strony treść zaczyna się pod nagłówkiem bieżącym
    const lineH = 4.6;      // interlinia tekstu akapitowego (9.5 pt)

    let currentY = M.top;

    const ensureSpace = (requiredHeight: number): void => {
      if (currentY + requiredHeight > pageHeight - M.bottom) {
        pdf.addPage();
        currentY = nextPageTop;
      }
    };

    // --- Nagłówek dokumentu (tylko 1. strona) ---
    pdf.setFont('Lato', 'bold');
    pdf.setFontSize(19);
    pdf.setTextColor(EXPORT_PALETTE.textPrimary);
    pdf.text('Raport diagnostyczny WISC-V', M.left, currentY + 2);
    currentY += 6;

    pdf.setDrawColor(EXPORT_PALETTE.accent);
    pdf.setLineWidth(0.9);
    pdf.line(M.left, currentY, M.left + contentW, currentY);
    currentY += 6;

    pdf.setFont('Lato', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(EXPORT_PALETTE.textTertiary);
    pdf.text(
      `Data badania: ${data.patientInfo.testDate || 'brak danych'}    •    Data wygenerowania: ${new Date().toLocaleDateString('pl-PL')}`,
      M.left,
      currentY
    );
    currentY += 11;

    // --- Helpery layoutu ---

    // Nagłówek sekcji: pionowy znacznik akcentowy + tytuł bold w kolorze
    // semantycznym. ensureSpace z zapasem na min. 2 linie treści, żeby
    // nagłówek nie zostawał samotnie na dole strony.
    const sectionHeading = (title: string, color: string, minContentH = 14): void => {
      ensureSpace(8 + minContentH);
      pdf.setFillColor(color);
      pdf.rect(M.left, currentY - 3.4, 1.5, 4.4, 'F');
      pdf.setFont('Lato', 'bold');
      pdf.setFontSize(12.5);
      pdf.setTextColor(color);
      pdf.text(title, M.left + 4.2, currentY);
      currentY += 7;
    };

    // Wspólne style autoTable (font Lato, cienkie linie stone-200)
    const autoTableCommon = {
      margin: { left: M.left, right: M.right, top: nextPageTop, bottom: M.bottom },
      styles: {
        font: 'Lato',
        fontSize: 9.5,
        textColor: EXPORT_PALETTE.textPrimary,
        lineColor: EXPORT_PALETTE.borderSubtle,
        lineWidth: 0.15,
        cellPadding: { top: 2, bottom: 2, left: 2.5, right: 2.5 },
      },
    };

    const writeParagraph = (text: string): void => {
      pdf.setFont('Lato', 'normal');
      pdf.setFontSize(9.5);
      pdf.setTextColor(EXPORT_PALETTE.textPrimary);
      // Treść AI bywa wieloakapitowa (\n) — każdy akapit łamiemy osobno
      // i oddzielamy światłem, żeby tekst nie zlewał się w jeden blok.
      const paragraphs = text.split(/\n+/).map(p => p.trim()).filter(Boolean);
      paragraphs.forEach((para, pIdx) => {
        if (pIdx > 0) currentY += 2.4; // światło między akapitami
        const lines: string[] = pdf.splitTextToSize(para, contentW);
        lines.forEach(line => {
          ensureSpace(lineH);
          pdf.text(line, M.left, currentY);
          currentY += lineH;
        });
      });
    };

    const writeBullets = (items: string[]): void => {
      pdf.setFontSize(9.5);
      items.forEach(item => {
        const lines: string[] = pdf.splitTextToSize(item, contentW - 5.5);
        lines.forEach((line, idx) => {
          ensureSpace(lineH);
          if (idx === 0) {
            pdf.setFont('Lato', 'bold');
            pdf.setTextColor(EXPORT_PALETTE.accent);
            pdf.text('•', M.left + 1, currentY);
          }
          pdf.setFont('Lato', 'normal');
          pdf.setTextColor(EXPORT_PALETTE.textPrimary);
          pdf.text(line, M.left + 5.5, currentY);
          currentY += lineH;
        });
        currentY += 1.2; // światło między punktami
      });
    };

    // --- Dane pacjenta: dwukolumnowa tabelka, cienkie linie ---
    sectionHeading('Dane pacjenta', EXPORT_PALETTE.accent);

    const patientData = [
      ['Imię i nazwisko', data.patientInfo.name || 'Brak danych'],
      ['Data urodzenia', data.patientInfo.birthDate || 'Brak danych'],
      ['Data badania', data.patientInfo.testDate || 'Brak danych'],
      ['Badający', data.patientInfo.examiner || 'Brak danych'],
      ['Instytucja', data.patientInfo.institution || 'Brak danych']
    ];

    (pdf as any).autoTable({
      ...autoTableCommon,
      startY: currentY,
      body: patientData,
      theme: 'grid',
      tableLineColor: EXPORT_PALETTE.border,
      tableLineWidth: 0.2,
      columnStyles: {
        0: {
          cellWidth: 48,
          fontStyle: 'bold',
          textColor: EXPORT_PALETTE.textSecondary,
          fillColor: EXPORT_PALETTE.headerBg,
        },
        1: { cellWidth: 'auto' },
      },
    });
    currentY = (pdf as any).lastAutoTable.finalY + 11;

    // --- Tabele wyników (wskaźniki złożone + podtesty) ---
    const scoreTable = (
      head: [string, string, string],
      rows: Array<[string, string, string]>,
      zebraFill: string
    ): void => {
      (pdf as any).autoTable({
        ...autoTableCommon,
        startY: currentY,
        head: [head],
        body: rows,
        theme: 'grid',
        tableLineColor: EXPORT_PALETTE.border,
        tableLineWidth: 0.2,
        headStyles: {
          fillColor: EXPORT_PALETTE.headerBg,
          textColor: EXPORT_PALETTE.accent,
          fontStyle: 'bold',
          fontSize: 10,
          lineColor: EXPORT_PALETTE.border,
          lineWidth: 0.15,
        },
        alternateRowStyles: { fillColor: zebraFill },
        columnStyles: {
          0: { cellWidth: 62 },
          1: { cellWidth: 20, halign: 'right' },
          2: { cellWidth: 'auto' },
        },
      });
      currentY = (pdf as any).lastAutoTable.finalY + 11;
    };

    sectionHeading('Wyniki wskaźników złożonych', EXPORT_PALETTE.accent);
    scoreTable(
      ['Wskaźnik', 'Wynik', 'Interpretacja'],
      this.collectScoreRows(data.compositeScores, data.compositeNames, data.getScoreInterpretation),
      EXPORT_PALETTE.rowAlt
    );

    sectionHeading('Wyniki podtestów', EXPORT_PALETTE.accent);
    scoreTable(
      ['Podtest', 'Wynik', 'Interpretacja'],
      this.collectScoreRows(data.subtestScores, data.subtestNames, data.getScoreInterpretation, 'subtest'),
      EXPORT_PALETTE.accentSoft
    );

    // --- Sekcje interpretacji (kolory semantyczne z EXPORT_PALETTE) ---
    const interpretationSections: Array<{
      title: string;
      content: string | string[];
      color: string;
    }> = [
      { title: 'Ogólna ocena', content: data.interpretation.general_assessment, color: EXPORT_PALETTE.textPrimary },
      { title: 'Mocne strony', content: data.interpretation.strengths, color: EXPORT_PALETTE.success },
      { title: 'Obszary wymagające wsparcia', content: data.interpretation.areas_for_support, color: EXPORT_PALETTE.error },
      { title: 'Funkcjonowanie w klasie', content: data.interpretation.classroom_implications, color: EXPORT_PALETTE.info },
      { title: 'Zalecenia dla nauczycieli', content: data.interpretation.teacher_recommendations, color: EXPORT_PALETTE.warning },
      { title: 'Zalecenia dla rodziców', content: data.interpretation.parent_recommendations, color: EXPORT_PALETTE.textSecondary }
    ];

    interpretationSections.forEach(section => {
      sectionHeading(section.title, section.color);
      if (Array.isArray(section.content)) {
        if (section.content.length > 0) {
          writeBullets(section.content);
        } else {
          writeParagraph('Brak danych');
        }
      } else {
        writeParagraph(section.content || 'Brak danych');
      }
      currentY += 6; // światło między sekcjami
    });

    // --- ZASTRZEŻENIE (wymóg prawny, pełna treść AI_DISCLAIMER) ---
    // Subtelna ramka na tle headerBg — nie da się przeoczyć, a nie krzyczy.
    pdf.setFontSize(8.5);
    const disclaimerLines: string[] = pdf.splitTextToSize(AI_DISCLAIMER, contentW - 9);
    const disclaimerLineH = 4;
    const boxH = 13 + disclaimerLines.length * disclaimerLineH;
    ensureSpace(boxH + 2);

    pdf.setFillColor(EXPORT_PALETTE.headerBg);
    pdf.setDrawColor(EXPORT_PALETTE.border);
    pdf.setLineWidth(0.2);
    pdf.roundedRect(M.left, currentY, contentW, boxH, 1.5, 1.5, 'FD');

    let boxY = currentY + 6;
    pdf.setFont('Lato', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(EXPORT_PALETTE.textSecondary);
    pdf.text('ZASTRZEŻENIE', M.left + 4.5, boxY);
    boxY += 5;

    pdf.setFont('Lato', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(EXPORT_PALETTE.textSecondary);
    disclaimerLines.forEach(line => {
      pdf.text(line, M.left + 4.5, boxY);
      boxY += disclaimerLineH;
    });
    currentY += boxH;

    // --- Nagłówek bieżący (strony 2+) i stopka (każda strona) ---
    const pageCount = pdf.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      if (i > 1) {
        this.addRunningHeader(pdf, data.patientInfo.name);
      }
      this.addFooter(pdf, i, pageCount);
    }

    return pdf;
  }

  // Nagłówek bieżący na stronach 2+: tytuł raportu + pacjent, cienka linia.
  private static addRunningHeader(pdf: jsPDF, patientName: string): void {
    const pageWidth = pdf.internal.pageSize.getWidth();

    pdf.setFont('Lato', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(EXPORT_PALETTE.textTertiary);
    pdf.text('Raport diagnostyczny WISC-V', 18, 14);
    if (patientName) {
      pdf.text(patientName, pageWidth - 18, 14, { align: 'right' });
    }

    pdf.setDrawColor(EXPORT_PALETTE.borderSubtle);
    pdf.setLineWidth(0.3);
    pdf.line(18, 16.2, pageWidth - 18, 16.2);
  }

  // Stopka na każdej stronie: skrócony disclaimer + numer strony X z Y.
  private static addFooter(pdf: jsPDF, pageNum: number, totalPages: number): void {
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();

    pdf.setDrawColor(EXPORT_PALETTE.borderSubtle);
    pdf.setLineWidth(0.3);
    pdf.line(18, pageHeight - 16, pageWidth - 18, pageHeight - 16);

    pdf.setFont('Lato', 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(EXPORT_PALETTE.textTertiary);
    pdf.text(this.PDF_FOOTER_DISCLAIMER, 18, pageHeight - 11.5);
    pdf.text(`Strona ${pageNum} z ${totalPages}`, pageWidth - 18, pageHeight - 11.5, { align: 'right' });
  }

  // Helper function to format recommendations for DOCX
  private static formatRecommendations(docx: DocxModule, recommendations: string[] | string): DocxParagraph[] {
    const { Paragraph, TextRun } = docx;
    if (Array.isArray(recommendations)) {
      return recommendations.map(rec => new Paragraph({
        children: [
          new TextRun({
            text: "• ",
            bold: true,
            size: 24,
            color: docxHex(EXPORT_PALETTE.textSecondary)
          }),
          new TextRun({
            text: rec,
            size: 24
          })
        ],
        spacing: { after: 150, before: 0 },
        indent: { left: 360, hanging: 180 }
      }));
    } else {
      return [new Paragraph({
        text: recommendations || 'Brak danych',
        spacing: { after: 300, before: 0 },
        indent: { left: 200 }
      })];
    }
  }
}